<template>
  <div :class="prefixCls" class="relative">
    <section class="flex items-center justify-between mb-1">
      <span :class="{ 'opacity-0': !label }" class="label">{{ label }}</span>
      <Tooltip
        :visible="visible"
        :placement="placement"
        color="var(--component-bg-color3)"
        overlayClassName="!max-w-70"
      >
        <template #title>
          <div
            v-for="(item, idx) in getValid"
            :key="idx"
            class="flex items-start"
            :class="{
              'color-primary': item.valid,
              'color-danger': !item.valid,
            }"
          >
            <IconFont :name="item.icon" class="mt-1" />
            <span class="ml-1 color-64">{{ item.title }}</span>
          </div>
          <div>
            <IconFont name="M-terminal-iconinformation_line" />
            <span class="ml-1 color-64">{{ t('sys.login.passwordRules.ruleTip') }}</span>
          </div>
        </template>
        <div :class="`${prefixCls}-bar`" class="flex items-center">
          <div
            :class="`${prefixCls}-bar--fill`"
            class="fill-1"
            :style="getComputedStyle('weak')"
          ></div>
          <div
            :class="`${prefixCls}-bar--fill`"
            class="fill-2"
            :style="getComputedStyle('middle')"
          ></div>
          <div
            :class="`${prefixCls}-bar--fill`"
            class="fill-3"
            :style="getComputedStyle('strong')"
          ></div>
        </div>
      </Tooltip>
    </section>
    <InputPassword
      v-if="showInput"
      v-bind="$attrs"
      allowClear
      :value="innerValueRef"
      @change="handleChange"
      :disabled="disabled"
      @blur="handleBlur"
      @focus="handleFocus"
    >
      <template #[item]="data" v-for="item in Object.keys($slots)">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
    </InputPassword>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, ref, watch, unref, watchEffect, StyleValue } from 'vue';
  import { Input, Tooltip } from 'ant-design-vue';
  import { zxcvbn } from '@zxcvbn-ts/core';
  import { useDesign } from '/@/hooks/web/useDesign';
  import { propTypes } from '/@/utils/propTypes';
  import { PASSWORD_REG_TWO } from '/@/utils/regExps';
  import { IconFont } from '/@/components/Icon';
  import { TooltipPlacement } from 'ant-design-vue/lib/tooltip';
  import { VueTypeDef } from 'vue-types';
  import { useI18n } from '@mcex/locale';

  const SuccessIcon = 'M-terminal-iconsuccess';
  const ErrIcon = 'M-terminal-iconclose';
  enum StrengthEnum {
    weak = 0,
    middle = 1,
    strong = 2,
  }
  /**
   * 强度区间
   *  0-4 弱
   * 4-8 中
   * 8-10 强
   */
  const StrongRange = {
    weak: [0, 4],
    middle: [4, 8],
    strong: [8, 10],
  };

  type RuleType = {
    title: () => string;
    regx: RegExp;
  };

  export default defineComponent({
    name: 'StrengthMeter',
    components: { InputPassword: Input.Password, Tooltip, IconFont },
    props: {
      value: propTypes.string,
      showInput: propTypes.bool.def(true),
      disabled: propTypes.bool.def(false),
      label: propTypes.string.def(''),
      placement: propTypes.oneOf<TooltipPlacement[]>(['top', 'right']).def('top'),
      rules: propTypes.array.def(undefined) as VueTypeDef<RuleType[]>,
    },
    emits: ['score-change', 'change', 'focus'],
    setup(props, { emit }) {
      const { t } = useI18n();
      const defaultRules: RuleType[] = [
        {
          title: () => t('sys.login.passwordRules.rule1'),
          regx: /^.{6,20}$/,
        },
        {
          title: () => t('sys.login.passwordRules.rule2'),
          regx: PASSWORD_REG_TWO,
        },
      ];

      const innerValueRef = ref('');
      const { prefixCls } = useDesign('strength-meter');

      const getValid = computed(() => {
        const val = unref(innerValueRef);
        return (props.rules || defaultRules).map((item) => {
          const valid = item.regx.test(val);
          return {
            valid,
            title: item.title?.(),
            icon: valid ? SuccessIcon : ErrIcon,
          };
        });
      });

      const guessScore = ref(-1);

      const visible = ref(false);

      function handleChange(e: ChangeEvent) {
        innerValueRef.value = e.target.value;
      }

      watchEffect(() => {
        innerValueRef.value = props.value || '';
      });

      watch(
        () => unref(innerValueRef),
        (val) => {
          const score = val ? zxcvbn(unref(val)).guessesLog10 : -1;
          guessScore.value = score;
          emit('score-change', score);
          emit('change', val);
        },
      );
      // 获取强度
      const getStrength = computed(() => {
        // 如果校验未全部通过，则只显示弱
        if (unref(getValid)?.some((item) => !item.valid)) return 'weak';
        const score = unref(guessScore);
        console.log('🚀 ~ file: StrengthMeter.vue:158 ~ getStrength ~ score:', score);
        if (score >= 0 && score <= 4) return 'weak';
        if (score > 4 && score <= 8) return 'middle';
        if (score > 8) return 'strong';
        return 'weak';
      });
      /**
       * 根据强度获取颜色
       */
      const getColor = computed(() => {
        const strength = unref(getStrength);
        if (strength === 'weak') return 'var(--danger-color)';
        if (strength === 'middle') return 'var(--warning-color2)';
        if (strength === 'strong') return 'var(--primary-color-2)';
        return '#000';
      });

      const getComputedStyle = (type) => {
        // 如果校验未全部通过，则只显示弱
        if (unref(getValid)?.some((item) => !item.valid) && type !== 'weak') {
          return {
            '--width': '0',
            '--trans': trans.value[StrengthEnum[type]],
          } as StyleValue;
        }
        return {
          '--width': guessScore.value >= StrongRange[type][0] ? '24px' : '0',
          '--trans': trans.value[StrengthEnum[type]],
        } as StyleValue;
      };

      /**
       * 监听强度，根据强度前后变化值获取延时
       */
      const trans = ref<string[]>([]);
      watch(
        () => unref(getStrength),
        (val, oldVal) => {
          let delay = ['', '', ''];
          // 由弱变强，则分别延时0.5s，1s
          if (oldVal === 'weak' && val === 'strong') {
            delay = ['', '0.5s', '1s'];
          }
          // 由中变强，则延时0.5s
          if (oldVal === 'middle' && val === 'strong') {
            delay = ['', '', '0.5s'];
          }
          // 由强变弱，则延时0.5s
          if (oldVal === 'strong' && val === 'weak') {
            delay = ['1s', '0.5s', ''];
          }
          // 由强变中，则延时0.5s
          if (oldVal === 'strong' && val === 'middle') {
            delay = ['', '', ''];
          }
          trans.value = [
            `width 0.5s ease-in-out ${delay[0]}, background 0.5s linear ${delay[0]}`,
            `width 0.5s ease-in-out ${delay[1]}, background 0.5s linear ${delay[1]}`,
            `width 0.5s ease-in-out ${delay[2]}, background 0.5s linear ${delay[2]}`,
          ];
        },
      );

      const handleFocus = (e) => {
        visible.value = true;
        emit('focus', e);
      };
      const handleBlur = (_e) => {
        visible.value = false;
      };

      return {
        handleChange,
        prefixCls,
        innerValueRef,
        getColor,
        getComputedStyle,
        visible,
        getValid,
        handleFocus,
        handleBlur,
        trans,
        t,
      };
    },
  });
</script>
<style lang="less" scoped>
  @prefix-cls: ~'@{namespace}-strength-meter';

  .@{prefix-cls} {
    @fillWidth: 24px;
    @h: 3px;
    @barHeight: 22px;

    .label {
      .text-sm(400);
      color: @text-color-64;
    }
    // 强度条
    &-bar {
      width: 80px;
      height: @barHeight;
      line-height: @barHeight;
      border-radius: 6px;

      &--fill {
        position: relative;
        width: @fillWidth;
        height: @h;
        border-radius: inherit;
        background-color: #000;

        &:not(:last-child) {
          margin-right: 4px;
        }

        &::before {
          content: ' ';
          position: absolute;
          height: inherit;
          border-radius: inherit;
          width: var(--width);
          background-color: v-bind(getColor);
          transition: var(--trans);
        }
      }
    }
  }
</style>
